import { GtvSchemaType, type SubtitlesForEpisode } from '@inkitt/titan/types/galateatvTypes';
import type { LanguageCode } from '@inkitt/types-galateatv';
import { z } from 'zod';

export type CuratedEpisode = Pick<
  GtvSchemaType['Episode'],
  'id' | 'seriesId' | 'number' | 'title' | 'posterUrl' | 'cost' | 'subtitlesUrl'
> & {
  protectedStreamUrl: string;
  horizontalStreamUrl: string;
  horizontalPosterUrl: string;
  liquidStreamUrl: string;
  liquidPosterUrl: string;
  durationMS?: number;
  subtitles?: SubtitlesForEpisode;
  /**
   * Language of the subtitle track served above, null when the episode ships none. Optional
   * because clients can rehydrate persisted responses cached before the field existed.
   */
  subtitleLanguage?: LanguageCode | null;
};

export const UnlockEpisodeBody = z.object({
  deviceUnixTime: z.number().min(0),
  branchId: z.number().min(0),
  branchName: z.string(),
  episodeNumber: z.number().min(0),
  cost: z.number().min(0),
  /** Whether the current episode is being auto unlocked */
  autoUnlocked: z.boolean(),
  /** Whether the next episode should be auto unlocked */
  autoUnlockNextEpisode: z.boolean(),
  /** Whether the current episode is being unlocked after the countdown timer elapses */
  isCountdownUnlocked: z.boolean().optional(),
  /** @deprecated not used anymore since unlimited users do not "unlock" episodes anymore rather we send them the entire playlist */
  hasActiveUnlimitedSubscription: z.boolean().optional(),
  /** @deprecated not used anymore */
  isUnlimitedSandboxEntitlement: z.boolean().optional(),
  /**
   * Whether admin user is using a simulated subscription.
   * @deprecated not used anymore since unlimited users do not "unlock" episodes anymore rather we send them the entire playlist
   */
  isSimulatedSubscription: z.boolean().optional(),
  /** @deprecated ignored by the server; still sent by released apps */
  overrideTestWaitTimerMetadataVariant: z.string().optional(),
  /** testPricingVariant overridden value (used for QA/dev-menu) */
  overrideTestPricingVariant: z.string().optional(),
  /** user timezone offset, need for the calculation next unlock time by the depends from the user local time in new wait timer tests */
  userTimezoneOffset: z.number().optional(),
});

export type UnlockEpisodeBody = z.infer<typeof UnlockEpisodeBody>;

// TODO: we need to consolidate a clientfacing episode type.
export type UnlockEpisodeResponse = {
  protectedStreamUrl: string;
  horizontalStreamUrl: string;
  liquidStreamUrl: string;
  nextEpisodes?: CuratedEpisode[];
  branchData: GtvSchemaType['BranchData'];
};
